// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import * as sdk from '@canton-network/dapp-sdk';
import { useEffect, useState } from 'react';

/**
 * React hook that manages the connection to the wallet gateway.
 * Uses the dapp-sdk to connect and disconnect, and updates the connection status.
 *
 * @returns { connect, disconnect, status }
 */
export function useConnect(): {
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  status?: sdk.dappAPI.StatusEvent;
} {
  const [status, setStatus] = useState<sdk.dappAPI.StatusEvent>();

  async function connect() {
    await sdk
      .connect()
      .then(setStatus)
      .catch((err) => {
        console.error('Error connecting to wallet:', err);
        throw err;
      });
  }

  async function disconnect() {
    await sdk.disconnect().then(() => {
      setStatus(undefined);
    });
  }

  useEffect(() => {
    sdk
      .status()
      .then(setStatus)
      .catch(() => {
        setStatus(undefined);
      });
  }, []);

  useEffect(() => {
    if (status?.isConnected) {
      const onStatusChanged = (s: sdk.dappAPI.StatusEvent) => {
        setStatus(s);
      };

      sdk.onStatusChanged(onStatusChanged).catch(console.error);

      return () => {
        sdk.removeOnStatusChanged(onStatusChanged).catch(console.error);
      };
    }

    return () => {};
  }, [status]);

  return {
    connect,
    disconnect,
    status,
  };
}
